import { clone } from "ramda";
import { type FC, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import type { ISelectionModel } from "@/app-utils/selection-model.ts";
import type { Dictionary, IStringAnyMap } from "@/app-utils/type.ts";
import type { IndexStatus } from "@/graphql.ts";
import { type ProjectFile } from "@/graphql.ts";
import { fileSearchGridColumnDefs } from "@/modules/files/settings/column-defs.ts";
import { useFileSearchStore } from "@/modules/files/stores/file-search.js";
import { getSortOrderExpressionFromTableColumns } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import DataContextIndicator from "@/ui-modules/core/grid/DataContextIndicator.tsx";
import InMemoryTableWithSelectionModel from "@/ui-modules/core/grid/InMemoryTableWithSelectionModel.tsx";

import type { FileBrowserProps, IdentifiableFileData } from "./FileBrowser.js";

export interface FileSearchResultsProps {
  isSelectableFilter?: FileBrowserProps["isSelectableFilter"];
  singleSelection?: boolean;
}

export interface FileNameCellParams {
  fileSearchContext?: boolean;
}
const cellRendererParamsLookup: IStringAnyMap = {
  // FIXME Circular deps?
  // [fileBrowserGridColumnDefs[0].field]: { fileSearchContext: true } satisfies FileNameCellParams,
  file: { fileSearchContext: true } satisfies FileNameCellParams,
};

export const FileSearchResults: FC<FileSearchResultsProps> = (props) => {
  const files = useFileSearchStore((state) => state.files);
  const totalResultsCount = useFileSearchStore((state) => state.totalResultsCount);
  const isLoading = useFileSearchStore((state) => state.loading);
  const selectFiles = useFileSearchStore((state) => state.selectFiles);
  const selectedFiles = useFileSearchStore((state) => state.selectedFiles);
  const clearStore = useFileSearchStore((state) => state.clear);
  const setSortExpression = useFileSearchStore((state) => state.setSortExpression);

  const indexingStatusLookup = useSelector<IAppState, Dictionary<IndexStatus>>(
    (state) => state.files?.indexingStatusLookup,
  );

  const [fileRows, setFileRows] = useState<IdentifiableFileData[]>([]); // type defined in buildFileRowData

  // Create the data for the Table
  useEffect(() => {
    const children = clone(files); // because of the sorting
    const rows = children
      //?.sort(defaultFileBrowserSorting)
      ?.map((file) => {
        const isSelectable = props.isSelectableFilter ? props.isSelectableFilter(file) : true;

        return {
          uuid: String(file.id),
          id: file.id,
          isSelectable: isSelectable,
          file: {
            ...file,
            indexingStatus: indexingStatusLookup[file.id] || file.indexingStatus,
          },
        } satisfies IdentifiableFileData;
      });
    //.filter((file) => genericFilter(fileBrowserGridColumnDefs, file, filter));
    setFileRows(rows ?? []);
  }, [files, indexingStatusLookup]);

  const selectionModelUpdated = useCallback(
    (selectionModel: ISelectionModel) => {
      const selectedFiles: ProjectFile[] = selectionModel.selectedIds
        .map((id) => files?.find((file) => file.id === id))
        .filter((file) => !!file);
      selectFiles(selectedFiles);
    },
    [files, selectFiles],
  );

  useEffect(() => {
    return clearStore;
  }, []);

  return (
    <div style={{ position: "relative", flex: "1 1 auto", display: "flex" }} data-testid="file-search">
      <InMemoryTableWithSelectionModel
        key={"file-search"}
        className={"file-browser"}
        isLoading={isLoading}
        uniqueGridIdentifier="file-search"
        columns={fileSearchGridColumnDefs}
        rowGridData={fileRows}
        useSelectionModel
        allowRowSelection={false} // not compatible with the folder selection ! to be consistent let's forbid the usage of row click selection in file picker
        selectionModelUpdated={selectionModelUpdated}
        gridProps={{
          getRowNodeId: (data) => data.id,
        }}
        noRowsMessage={"Filter returns no match"}
        //sortingField={sorting ?? ""}
        onSortModified={(sortedColumns) => {
          setSortExpression(getSortOrderExpressionFromTableColumns(sortedColumns));
        }}
        exclusiveSelection={props.singleSelection}
        cellRendererParamsLookup={cellRendererParamsLookup}
      />
      {!isLoading && (
        <DataContextIndicator
          key="context-indicator"
          numTotalRows={totalResultsCount}
          numRowsFiltered={files?.length}
          numRowsSelected={selectedFiles?.length}
          rowEntityLabel={"file"}
          filterIsSet={totalResultsCount != files?.length}
        />
      )}
    </div>
  );
};
